import {
  ArrowUpRight,
  CalendarCheck,
  GraduationCap,
  HandHeart,
  ShieldCheck,
  Sprout,
  Store,
  TrendingUp,
  Video,
} from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';
import { EMAIL, IMG, PHONE, PHONE_HREF, SKOOL_URL } from '../data/site';

const HUB_EMAIL = `MassagePracticeHub@thuladoulamassage.com`;

const AUDIENCE = [
  {
    icon: GraduationCap,
    title: 'New LMT graduates',
    text: 'Licensed and ready — but school didn’t teach the business. Learn to run the practice side with confidence.',
  },
  {
    icon: Store,
    title: 'Therapists going independent',
    text: 'Leaving the spa or franchise to build something of your own? Set up pricing, policies, and systems that protect your energy.',
  },
  {
    icon: ShieldCheck,
    title: 'Practices adding medical massage',
    text: 'Referrals, documentation, and billing readiness — bring medical massage into your practice the compliant way.',
  },
  {
    icon: Sprout,
    title: 'Doulas expanding services',
    text: 'Already supporting families? Add wellness services that extend your care — and your calendar.',
  },
];

const PILLARS = [
  {
    num: '01',
    icon: Store,
    title: 'Business foundations',
    text: 'Pricing, scheduling, and policies that keep your calendar full and your boundaries intact — the unglamorous basics that decide whether a practice survives.',
  },
  {
    num: '02',
    icon: ShieldCheck,
    title: 'Compliance & documentation',
    text: 'Medical massage paperwork, referral workflows, and billing readiness — including what it takes to accept programs like VA Community Care.',
  },
  {
    num: '03',
    icon: HandHeart,
    title: 'Client experience & retention',
    text: 'Intakes that inform treatment, communication that builds trust, and the small touches that turn a first visit into a decade of loyalty.',
  },
  {
    num: '04',
    icon: TrendingUp,
    title: 'Growth & referrals',
    text: 'Positioning in your local market, building relationships with referring providers, and marketing that stays ethical and true to your craft.',
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Join the interest list',
    text: 'Email us or send a message through the contact form — tell us where your practice is today and where you want it to go.',
  },
  {
    num: '02',
    title: 'Discovery conversation',
    text: 'We’ll talk through your goals, your gaps, and whether the Hub is the right fit — no pressure, no pitch script.',
  },
  {
    num: '03',
    title: 'Your coaching plan',
    text: 'Coaching shaped around your practice — the pillars above, prioritized for what you actually need next.',
  },
];

export default function PracticeHub() {
  return (
    <>
      <SEO
        title="Massage Practice Hub — Coaching for Therapists | Thula Doula Wellness"
        description="Massage Practice Hub, powered by Thula Doula Wellness: coaching for massage therapists and wellness practitioners — business foundations, compliance, client experience, and growth."
      />
      <PageHeader
        testId="hub-page-header"
        eyebrow="Coaching Program"
        lines={['Massage Practice Hub.', 'Powered by Thula Wellness.']}
        sub="Coaching for massage therapists and wellness practitioners ready to build a practice that’s clinical, compliant, and full of heart."
        cta={false}
      />

      <section className="bg-brand-cream pb-8" data-testid="hub-availability">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div
              data-testid="hub-interest-banner"
              className="flex flex-col items-start gap-5 rounded-3xl border border-brand-teal/15 bg-brand-sage/40 p-7 md:flex-row md:items-center md:justify-between md:p-9"
            >
              <p className="text-sm font-medium text-brand-ink md:text-base">
                <Sprout className="mr-2 inline size-4 text-brand-teal" />
                The Hub is new — the <strong>early interest list is open</strong>. Founding members
                help shape the program.
              </p>
              <a
                data-testid="hub-interest-btn"
                href={SKOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
              >
                Join the interest list on Skool
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-16 md:py-24" data-testid="hub-audience">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['Who the Hub is for.']}
            className="font-display text-[clamp(2.2rem,4.5vw,3.75rem)] font-light tracking-tight text-brand-ink"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCE.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1}>
                <div className="h-full rounded-3xl bg-brand-parchment p-8 transition-transform duration-500 hover:-translate-y-1">
                  <a.icon className="size-7 text-brand-teal" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-2xl font-light tracking-tight text-brand-ink">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-moss">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-20 md:py-28" data-testid="hub-pillars">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['What the Hub covers.']}
            className="font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
          />
          <div className="mt-14 border-t border-brand-ink/10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.05}>
                <div className="grid grid-cols-1 gap-6 border-b border-brand-ink/10 py-10 md:grid-cols-12 md:items-start">
                  <span className="font-display text-lg italic text-brand-teal/60 md:col-span-1">
                    {p.num}
                  </span>
                  <div className="flex items-center gap-4 md:col-span-4">
                    <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-teal text-brand-cream">
                      <p.icon className="size-5" strokeWidth={1.75} />
                    </span>
                    <h3 className="font-display text-3xl font-light tracking-tight text-brand-ink">
                      {p.title}
                    </h3>
                  </div>
                  <p className="max-w-2xl text-sm leading-relaxed text-brand-moss md:col-span-7 md:text-base">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28" data-testid="hub-mentor">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <ParallaxImage
                src="/mimi.jpg"
                alt="Mignon “Mimi” Fields, founder of Thula Doula Wellness and the Massage Practice Hub"
                className="aspect-[4/3] w-full rounded-3xl"
                strength={26}
              />
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-brand-moss/70">
                Mignon “Mimi” Fields — Founder &amp; Hub mentor
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <MaskedLinesOnView
              as="h2"
              lines={['Learn from a practice', 'built the way we teach.']}
              className="font-display text-4xl font-light leading-[1.08] tracking-tight text-brand-ink md:text-5xl"
            />
            <Reveal delay={0.15}>
              <div className="mt-7 max-w-2xl space-y-5 text-base leading-relaxed text-brand-moss md:text-lg">
                <p>
                  The Massage Practice Hub is led by Mignon “Mimi” Fields — Licensed Massage
                  Therapist, Certified Birth Doula, and founder of Thula Doula Wellness — whose
                  background spans healthcare, medical insurance compliance, maternal support, and
                  therapeutic wellness.
                </p>
                <p>
                  Everything the Hub teaches has been lived: building a referral-based medical
                  massage practice, working with programs like VA Community Care, and growing a
                  team around compassionate, individualized care.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hub-mentor-credentials">
                {['Licensed Massage Therapist', 'Certified Birth Doula', 'Insurance compliance background'].map(
                  (chip) => (
                    <span
                      key={chip}
                      className="rounded-full bg-brand-sage/40 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-teal"
                    >
                      {chip}
                    </span>
                  )
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-20 md:pb-28" data-testid="hub-steps">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="border-t border-brand-ink/10 pt-16 md:pt-24">
            <MaskedLinesOnView
              as="h2"
              lines={['How it works.']}
              className="font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
            />
            <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
              {STEPS.map((step, i) => (
                <Reveal key={step.num} delay={i * 0.12}>
                  <div className="border-t-2 border-brand-sage pt-6" data-testid={`hub-step-${i}`}>
                    <span className="font-display text-5xl font-light italic text-brand-teal/70">
                      {step.num}
                    </span>
                    <h3 className="mt-4 text-lg font-semibold text-brand-ink">{step.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-moss">{step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="hub-cta">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-brand-ink p-8 text-brand-cream md:p-14">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -left-24 -bottom-24 size-72 rounded-full bg-brand-teal/40 blur-3xl"
            />
            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <MaskedLinesOnView
                  as="h2"
                  lines={['Build the practice', 'you actually want.']}
                  className="font-display text-4xl font-light leading-[1.08] tracking-tight md:text-5xl"
                />
                <p className="mt-5 max-w-xl text-sm leading-relaxed text-brand-cream/75 md:text-base">
                  Join the Massage Practice Hub community on Skool to be first in line when the
                  founding cohort opens — or call {PHONE} with questions.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 lg:col-span-5 lg:items-end">
                <a
                  data-testid="hub-cta-interest-btn"
                  href={SKOOL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-brand-cream px-8 py-4 text-base font-semibold text-brand-ink transition-[background-color,transform] duration-300 hover:bg-brand-sage active:scale-[0.97]"
                >
                  Join the Hub on Skool <ArrowUpRight className="size-5" />
                </a>
                <a
                  data-testid="hub-cta-phone-link"
                  href={PHONE_HREF}
                  className="text-sm text-brand-cream/60 transition-colors duration-300 hover:text-brand-cream"
                >
                  Questions? Call {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
